import { useState } from 'react';

import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, Factory, Plus, Receipt, RotateCcw, Trash2, X } from 'lucide-react';
import { listInvoices, updateInvoiceStatus, updateInvoiceDeliveryDate, updateInvoiceTrial, updateInvoiceTrialDate, updateInvoiceReached, setOrderStage, restoreInvoice } from '../api/resources';
import { inr } from '../utils/format';
import { useToast } from '../context/ToastContext';
import { StatusBadge } from '../components/ui/Badge';
import DataTable from '../components/ui/DataTable';
import { STAGE_LABEL, STAGE_TONE, STAGE_DOT, HAND_MOVES } from '../components/invoice/StageControl';
import { QuickAddCustomer } from './InvoiceWizard';
import DateInput from '../components/ui/DateInput';
import PageHeader from '../components/ui/PageHeader';
import ChipPicker from '../components/ui/ChipPicker';
import OrderWorkPanel from '../components/invoice/OrderWorkPanel';
import { usePortalAccess } from '../hooks/usePortalAccess';
import { useAuth } from '../context/AuthContext';
import { isManager } from '../utils/roles';
import { forWhom } from '../utils/orderFor';

// The order's status follows the garments' stages: nothing started = Booked,
// anything under way = In Progress, everything delivered = Delivered. Only the
// two decisions the stages cannot make are offered by hand, so there is never
// a stage and a status saying different things.
const STATUS_OPTIONS = [
  { value: 'DELIVERED', label: 'Delivered', hint: 'hand it over', dot: 'bg-success-500' },
  { value: 'CANCELLED', label: 'Cancelled', dot: 'bg-danger-500' },
];
const STATUS_TITLE = 'Follows the production stages. Only "delivered" and "cancelled" are set by hand.';

/**
 * A cell you can change in place: the value itself is the control.
 *
 * This used to be a small pencil revealed on hover. That cannot work on a
 * phone — there is no hover to reveal it, and even pinned open it was a 20px
 * target buried inside a table that scrolls sideways. So the chip is the
 * button: the native `<select>`/`<input>` is laid transparently over the whole
 * thing, and a chevron says it can be changed.
 *
 * A native control rather than a styled panel: it is keyboard-driven, it gets
 * the platform's own picker on a phone, and it cannot be clipped by
 * `.card { overflow:hidden }` — which is why DataTable's own Menu needs a
 * portal and this does not.
 *
 * DataTable's row-click handler ignores events from `input` and `select`, so
 * changing a value here never also opens the invoice.
 */
function EditableCell({ title, busy, children, control }) {
  return (
    <span
      // A floor on both sides so even an empty value ("—") stays a thumb-sized
      // target; the desk machines get the tighter row back at `lg`.
      className={`relative -mx-1 inline-flex min-h-[2rem] min-w-[3rem] cursor-pointer items-center gap-1 rounded px-1
                  ring-1 ring-transparent transition-colors
                  hover:bg-surface-hover hover:ring-line-strong focus-within:ring-2 focus-within:ring-brand-400
                  lg:min-h-[1.75rem] lg:min-w-0
                  ${busy ? 'opacity-50' : ''}`}
      title={title}
    >
      {children}
      <ChevronDown size={11} className="shrink-0 text-content-subtle" />
      {control}
    </span>
  );
}

/** The status badge, which is also the picker. */
function StatusCell({ invoice, onChange, busy, disabled }) {
  if (disabled) return <StatusBadge status={invoice.status} />;
  return (
    <ChipPicker
      title={STATUS_TITLE}
      busy={busy}
      header="Set status"
      ariaLabel={`Set the status of invoice ${invoice.orderNo}`}
      options={STATUS_OPTIONS.filter((o) => o.value !== invoice.status)}
      onPick={onChange}
    >
      <StatusBadge status={invoice.status} />
    </ChipPicker>
  );
}

/**
 * How far the whole order has got, as one dot per garment.
 *
 * An order is not one thing — it is several garments moving at their own
 * speed, and the question a manager actually has is "how much of this is
 * finished, and is anything stuck?". Dots answer it without being read:
 * green is made, red is back for alteration, grey has not started.
 */
function ProgressCell({ invoice }) {
  const items = invoice.items || [];
  const total = Number(invoice.itemsTotal) || items.length;
  if (!total) return <span className="text-content-subtle">—</span>;
  const ready = Number(invoice.itemsReady) || 0;
  const alter = Number(invoice.itemsAlteration) || 0;
  const all = ready === total;

  const spread = items
    .map((i) => `${i.name}${i.qty > 1 ? ` ×${i.qty}` : ''} — ${STAGE_LABEL[i.stage] || 'not started'}`)
    .join('\n');

  return (
    <span className="inline-flex flex-col gap-0.5" title={spread}>
      <span className="flex items-center gap-1">
        <span className="flex gap-[3px]">
          {/* One dot per garment. Beyond eight the row would be wider than
              the count it illustrates, so the rest become "+n". */}
          {items.slice(0, 8).map((i, n) => (
            <span
              key={i.id ?? n}
              className={`h-[7px] w-[7px] rounded-full ${STAGE_DOT[i.stage] || 'bg-line-strong'}`}
            />
          ))}
          {items.length > 8 && <span className="text-2xs text-content-subtle">+{items.length - 8}</span>}
        </span>
        <span className={`text-xs font-semibold tabular-nums ${all ? 'text-success-700' : 'text-content'}`}>
          {ready}/{total}
        </span>
      </span>
      <span className="text-2xs text-content-subtle">
        {alter > 0
          ? <span className="font-semibold text-danger-600">{alter} in alteration</span>
          : all ? 'all ready' : 'ready'}
      </span>
    </span>
  );
}

/**
 * How many garments have reached the store, on one of their two journeys.
 *
 * A garment goes to the store twice — once for the customer to try on, and
 * again, after any alteration, to be collected — so this is a count, not a
 * yes/no: three kurtas can arrive on three different days.
 *
 * Read-only here. One garment at a time is ticked in the panel the caret
 * opens, which is also where the shop can see which one is still out.
 */
function AtStoreCell({ invoice, count, label, leg, onChange, busy, disabled }) {
  const total = Number(invoice.itemsTotal) || 0;
  const n = Number(count) || 0;
  if (!total) return <span className="text-content-subtle">—</span>;
  const all = n === total;
  const none = n === 0;
  const chip = (
    <span className={all ? 'badge-success' : none ? 'badge-neutral' : 'badge-info'}>{n}/{total}</span>
  );
  const title = all
    ? `All ${total} garment(s) are at the store ${label}`
    : `${n} of ${total} garment(s) at the store ${label} — open the row to tick one at a time`;
  if (disabled) return <span title={title}>{chip}</span>;
  return (
    <ChipPicker
      busy={busy}
      header={leg === 'trial' ? 'At the store for the trial' : 'Back at the store to collect'}
      title={title}
      ariaLabel={`At the store ${label} — invoice ${invoice.orderNo}`}
      options={[
        { value: 'yes', label: 'All at the store', hint: `every garment ${label}`, dot: 'bg-success-500', selected: all },
        { value: 'no', label: 'None yet', hint: 'still at the workshop', dot: 'bg-content-subtle', selected: none },
      ]}
      onPick={(v) => onChange(v === 'yes')}
    >
      {chip}
    </ChipPicker>
  );
}

// Whether the customer's trial has happened. An order with no trial booked has
// nothing to tick, so it shows a dash rather than a false "No".
const TRIAL_OPTIONS = [
  { value: 'yes', label: 'All tried on', hint: 'every garment on this order', dot: 'bg-success-500' },
  { value: 'no', label: 'None tried yet', hint: 'clears every garment', dot: 'bg-warning-500' },
];

function TrialCell({ invoice, onChange, busy, disabled }) {
  if (!invoice.trialDate) {
    return <span className="text-content-subtle" title="No trial booked for this order">—</span>;
  }
  // The customer tries the garments on one at a time, so this counts them.
  // The picker here is still the whole order — a garment at a time is ticked
  // in the panel the caret opens.
  const total = Number(invoice.itemsTotal) || 0;
  const tried = Number(invoice.trialDoneCount) || 0;
  const all = total > 0 && tried === total;
  const none = tried === 0;
  const chip = (
    <span className={all ? 'badge-success' : none ? 'badge-warning' : 'badge-info'}>
      {total ? `${tried}/${total}` : (all ? 'Yes' : 'No')}
    </span>
  );
  const title = all
    ? `All ${total} garment(s) tried on — booked for ${invoice.trialDate}`
    : `${tried} of ${total} garment(s) tried on — booked for ${invoice.trialDate}. Open the row to tick one at a time.`;
  if (disabled) return <span title={title}>{chip}</span>;
  return (
    <ChipPicker
      busy={busy}
      header={`Trial · ${invoice.trialDate}`}
      title={title}
      ariaLabel={`Trial for invoice ${invoice.orderNo}`}
      options={TRIAL_OPTIONS.map((o) => ({ ...o, selected: (o.value === 'yes') === all }))}
      onPick={(v) => onChange(v === 'yes')}
    >
      {chip}
    </ChipPicker>
  );
}

/** The trial date, which is also the date picker. */
function TrialDateCell({ invoice, onChange, busy, disabled }) {
  const date = invoice.trialDate;
  // A booked trial that has come and gone without being ticked is the one
  // worth colouring — it is a customer who did not turn up, or a tick nobody
  // remembered.
  const overdue = !!date && !invoice.trialDone && toNativeDate(date) < new Date().toISOString().slice(0, 10);
  const cls = overdue ? 'font-semibold text-danger-600' : (date ? '' : 'text-content-subtle');
  const title = !date
    ? 'No trial booked — pick a date'
    : overdue ? `Trial was ${date} and is not marked done` : `Trial booked for ${date}`;

  if (disabled) {
    return date ? <span className={cls} title={title}>{date}</span> : <span className="text-content-subtle">—</span>;
  }
  return (
    <EditableCell
      title={title}
      busy={busy}
      control={(
        <input
          type="date"
          className="absolute inset-0 cursor-pointer opacity-0"
          value={toNativeDate(date)}
          disabled={busy}
          aria-label={`Reschedule the trial for invoice ${invoice.orderNo}`}
          onChange={(e) => onChange(toDisplayDate(e.target.value))}
        />
      )}
    >
      <span className={`whitespace-nowrap ${cls}`}>{date || '—'}</span>
    </EditableCell>
  );
}

/** The delivery date, which is also the date picker. */
function DeliveryDateCell({ invoice, onChange, busy, disabled }) {
  const overdue = isOverdue(invoice);
  // Red only signals a missed deadline; a blank date stays muted.
  const dateClass = overdue
    ? 'font-semibold text-danger-600'
    : (invoice.deliveryDate ? '' : 'text-content-subtle');
  const overdueTitle = overdue ? 'Delivery date has passed' : undefined;

  if (disabled) {
    return invoice.deliveryDate
      ? <span className={dateClass} title={overdueTitle}>{invoice.deliveryDate}</span>
      : <span className="text-content-subtle">—</span>;
  }
  return (
    <EditableCell
      title={overdueTitle || 'Reschedule delivery'}
      busy={busy}
      control={(
        <input
          type="date"
          className="absolute inset-0 cursor-pointer opacity-0"
          // The list API returns dd-mm-yyyy; a native date input needs
          // yyyy-mm-dd.
          value={toNativeDate(invoice.deliveryDate)}
          disabled={busy}
          aria-label={`Reschedule delivery for invoice ${invoice.orderNo}`}
          onChange={(e) => onChange(toDisplayDate(e.target.value))}
        />
      )}
    >
      <span className={`whitespace-nowrap ${dateClass}`}>{invoice.deliveryDate || '—'}</span>
    </EditableCell>
  );
}

// 'dd-mm-yyyy' -> 'yyyy-mm-dd' for the native input.
const toNativeDate = (ddmmyyyy) => {
  const m = String(ddmmyyyy || '').match(/^(\d{2})-(\d{2})-(\d{4})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

// An order is overdue once its delivery date is in the past and it has been
// neither delivered nor cancelled — those two are closed, so a past date on
// them is just history, not a problem.
const CLOSED_STATUSES = ['DELIVERED', 'CANCELLED'];

function isOverdue(invoice) {
  if (!invoice?.deliveryDate) return false;
  if (CLOSED_STATUSES.includes(invoice.status)) return false;
  const iso = toNativeDate(invoice.deliveryDate);
  if (!iso) return false;
  // Compare date-only strings in the viewer's local calendar: an order due
  // today is not yet late, and a timestamp comparison would call it late from
  // 00:00 onwards.
  const now = new Date();
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
  return iso < today;
}
// 'yyyy-mm-dd' -> 'dd-mm-yyyy' for the API (parseDate accepts this form).
const toDisplayDate = (native) => {
  const m = String(native || '').match(/^(\d{4})-(\d{2})-(\d{2})$/);
  return m ? `${m[3]}-${m[2]}-${m[1]}` : '';
};

// The stage moves a person makes from the list. Cutting and stitching are not
// here: those follow the workers on their own.
// The one move table, shared with the per-garment control (StageControl).
const STAGE_MOVES = HAND_MOVES;

// The order is only as far along as its slowest garment; the rest of the
// spread sits in the tooltip so the column stays one line.
function StageCell({ invoice, onChange, busy, disabled }) {
  const stage = invoice.stage;
  if (!stage) return <span className="text-content-subtle">—</span>;
  const counts = invoice.stageCounts || {};
  const spread = Object.entries(counts).map(([k, n]) => `${STAGE_LABEL[k] || k}: ${n}`).join(' · ');
  const total = Object.values(counts).reduce((a, b) => a + b, 0);
  const here = counts[stage] || 0;
  const moves = STAGE_MOVES[stage] || [];
  const chip = (
    <>
      <span className={`rounded-full px-2 py-0.5 text-2xs font-bold uppercase tracking-wide ${STAGE_TONE[stage]}`}>
        {STAGE_LABEL[stage] || stage}
      </span>
      {total > here && <span className="text-2xs text-content-subtle">+{total - here}</span>}
    </>
  );

  if (disabled || !moves.length) {
    return <span className="inline-flex items-center gap-1" title={spread}>{chip}</span>;
  }
  return (
    <ChipPicker
      busy={busy}
      header="Move the garments to"
      title={`${spread}${here < total ? ' — a change moves the garments at this stage' : ''}`}
      ariaLabel={`Move order ${invoice.orderNo} to another stage`}
      options={moves.map((m) => ({ value: m, label: STAGE_LABEL[m] }))}
      onPick={onChange}
    >
      {chip}
    </ChipPicker>
  );
}

// Has the work on this order been handed out yet? Three states, because
// "some of it" is the one the shop actually needs to catch.
function WorkersCell({ invoice }) {
  const done = Number(invoice.jobsAssigned) || 0;
  const need = Number(invoice.jobsNeeded) || 0;
  if (!need) return <span className="text-content-subtle">—</span>;

  const all = done >= need;
  const none = done === 0;
  const tone = none ? 'badge-danger' : all ? 'badge-success' : 'badge-warning';
  const label = none ? 'None' : all ? 'All' : `${done}/${need}`;
  const title = none
    ? `Nobody is on this order yet — ${need} job(s) to hand out`
    : all
      ? `All ${need} job(s) handed out`
      : `${done} of ${need} job(s) handed out — ${need - done} still to go`;
  return <span className={tone} title={title}>{label}</span>;
}

const DATE_LABEL = { orderDate: 'order date', deliveryDate: 'delivery date', trialDate: 'trial date' };

// Which of the three dates the range filters on.
const DATE_FIELDS = [
  { value: 'orderDate', label: 'Order date', hint: 'when it was taken' },
  { value: 'deliveryDate', label: 'Delivery date', hint: 'when it is due out' },
  { value: 'trialDate', label: 'Trial date', hint: 'when the customer comes in' },
];

export function InvoiceList({ readOnly: viewerMode }) {
  // A portal login that may not take orders sees the same list, without the
  // buttons that would only be refused.
  const { can } = usePortalAccess();
  const { role } = useAuth();
  // Store / Workshop Manager: moving an order's status and stage along is
  // theirs whatever else is switched off, so the chips below stay live for
  // them even on a read-only list.
  const deskOnly = isManager(role);
  // Taking and changing an order is the "Take an order" switch — for a manager
  // desk as much as for anyone else held to a group.
  const readOnly = viewerMode || !can('createOrder');
  // Only the two roles the server lets restore are offered the trash.
  const maySeeTrash = ['ADMIN', 'SUPERVISOR'].includes(role);
  // 'active' is the order list; 'trash' is the same list of put-away orders.
  const [view, setView] = useState('active');
  const inTrash = view === 'trash';
  const nav = useNavigate();
  const qc = useQueryClient();
  const { success, error } = useToast();
  const [pendingId, setPendingId] = useState(null);
  const [pendingDateId, setPendingDateId] = useState(null);
  const [pendingStageId, setPendingStageId] = useState(null);
  const [pendingTrialId, setPendingTrialId] = useState(null);
  const [pendingTrialDateId, setPendingTrialDateId] = useState(null);
  const [pendingReached, setPendingReached] = useState(null);
  const [quickAdd, setQuickAdd] = useState(null);
  // Only a plain name (letters and spaces) is offered as a new customer —
  // numbers are invoice nos. or mobiles being looked up.
  const isName = (q) => /^[\p{L}\p{M} .]+$/u.test(q) && /\p{L}/u.test(q);

  const deliveryMut = useMutation({
    mutationFn: ({ id, deliveryDate }) => updateInvoiceDeliveryDate(id, deliveryDate),
    onMutate: ({ id }) => setPendingDateId(id),
    onSuccess: (d) => {
      success(d.message || 'Delivery date updated.');
      // The dashboard's delivery calendar reads these dates.
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not update the delivery date.'),
    onSettled: () => setPendingDateId(null),
  });

  // Moving an order on from the list. The server only moves the garments at
  // the stage shown, so a garment further ahead is not dragged back.
  const stageMut = useMutation({
    mutationFn: ({ id, stage }) => setOrderStage(id, { stage }),
    onMutate: ({ id }) => setPendingStageId(id),
    onSuccess: (d) => {
      success(d.message || 'Moved on.');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['production-board'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not move the order on.'),
    onSettled: () => setPendingStageId(null),
  });

  // The whole order's garments at once; one at a time is done in the panel.
  const reachedMut = useMutation({
    mutationFn: ({ id, leg, done }) => updateInvoiceReached(id, leg, done),
    onMutate: ({ id, leg }) => setPendingReached(`${id}-${leg}`),
    onSuccess: (d) => {
      success(d.message || 'Saved.');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['invoice-timeline'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not record this.'),
    onSettled: () => setPendingReached(null),
  });

  const trialDateMut = useMutation({
    mutationFn: ({ id, trialDate }) => updateInvoiceTrialDate(id, trialDate),
    onMutate: ({ id }) => setPendingTrialDateId(id),
    onSuccess: (d) => {
      success(d.message || 'Trial date updated.');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['supervisor-board'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not update the trial date.'),
    onSettled: () => setPendingTrialDateId(null),
  });

  const trialMut = useMutation({
    mutationFn: ({ id, done }) => updateInvoiceTrial(id, done),
    onMutate: ({ id }) => setPendingTrialId(id),
    onSuccess: (d) => {
      success(d.message || 'Saved.');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      // The supervisor's desk works down the same trials.
      qc.invalidateQueries({ queryKey: ['supervisor-board'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not record the trial.'),
    onSettled: () => setPendingTrialId(null),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, status }) => updateInvoiceStatus(id, status),
    onMutate: ({ id }) => setPendingId(id),
    onSuccess: (d) => {
      success(d.message || 'Status updated.');
      // The dashboard counts by status, so refresh it too.
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not update the status.'),
    onSettled: () => setPendingId(null),
  });
  // Date range still filters server-side; text search is handled by DataTable.
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [range, setRange] = useState({ from: '', to: '' });
  // Which date the range applies to — the shop chases delivery and trial dates
  // at least as often as the order date.
  const [dateField, setDateField] = useState('orderDate');

  const restoreMut = useMutation({
    mutationFn: (id) => restoreInvoice(id),
    onSuccess: (d) => {
      success(d.message || 'Order restored.');
      qc.invalidateQueries({ queryKey: ['invoices'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
    onError: (e) => error(e.response?.data?.message || 'Could not restore the order.'),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['invoices', range, view],
    queryFn: () => listInvoices({ ...range, page: 1, perPage: 1000, ...(inTrash ? { trashed: 1 } : {}) }),
  });
  const invoices = data?.invoices || [];

  const apply = () => setRange({ from, to, dateField });
  const clear = () => { setFrom(''); setTo(''); setRange({ from: '', to: '' }); };
  const hasRange = Boolean(range.from || range.to);

  // A trashed order is read-only until it is restored, so it always opens the
  // printed view rather than the editor.
  const open = (inv) => nav(readOnly || inTrash ? `/invoices/${inv.id}` : `/invoices/${inv.id}/edit`);

  const num = (v) => Number(v ?? 0);

  const columns = [
    {
      key: 'orderNo',
      // "No." rather than "Invoice No.": in an auto-layout table the header
      // text sets the floor, so the long word was the column's real width —
      // `w-16` alone could not shrink it.
      header: 'No.',
      hideable: false,
      width: 'w-16',
      className: 'whitespace-nowrap !px-2 text-xs',
      sortValue: (i) => num(i.orderNo) || i.orderNo,
      render: (i) => <span className="font-semibold text-brand-600">{i.orderNo}</span>,
    },
    {
      key: 'workers',
      header: 'Workers',
      width: 'w-20',
      // Sorts worst-first: the orders nobody is on are the ones to chase.
      sortValue: (i) => (Number(i.jobsNeeded) ? Number(i.jobsAssigned) / Number(i.jobsNeeded) : 2),
      exportValue: (i) => (Number(i.jobsNeeded) ? `${i.jobsAssigned}/${i.jobsNeeded}` : ''),
      render: (i) => <WorkersCell invoice={i} />,
    },
    {
      key: 'customer',
      header: 'Customer',
      width: 'w-36',
      className: 'max-w-[9rem] truncate',
      sortValue: (i) => i.customer?.name || '',
      exportValue: (i) => i.forLabel || i.customer?.name || '',
      // Who the clothes are for rides along with the name — the counter needs
      // to know before it opens the order, not after.
      render: (i) => (
        <span className="block">
          <span className="font-medium text-content">{i.customer?.name || '—'}</span>
          {i.familyMember && (
            <span className="block truncate text-2xs text-brand-700">{forWhom(i.familyMember)}</span>
          )}
        </span>
      ),
    },
    {
      key: 'mobile',
      header: 'Mobile',
      width: 'w-28',
      className: 'whitespace-nowrap',
      sortValue: (i) => i.customer?.mobile || '',
      exportValue: (i) => i.customer?.mobile || '',
      render: (i) => <span className="text-content-muted">{i.customer?.mobile || '—'}</span>,
    },
    {
      key: 'items',
      header: 'Items',
      width: 'w-40',
      className: 'max-w-[10rem] truncate',
      sortValue: (i) => i.itemsText || '',
      exportValue: (i) => i.itemsText || '',
      render: (i) => (i.itemsText
        ? <span className="text-content-muted" title={i.itemsText}>{i.itemsText}</span>
        : <span className="text-content-subtle">—</span>),
    },
    {
      key: 'orderDate',
      header: 'Invoice Date',
      width: 'w-24',
      className: 'whitespace-nowrap',
      // The list API already returns dd-mm-yyyy, so this renders it as-is.
      // Passing it through fmtDate again produced a blank cell: fmtDate feeds
      // the string to `new Date()`, which cannot read dd-mm-yyyy.
      sortValue: (i) => toNativeDate(i.orderDate) || '',
      exportValue: (i) => i.orderDate || '',
      render: (i) => i.orderDate || <span className="text-content-subtle">—</span>,
    },
    {
      key: 'deliveryDate',
      header: 'Delivery',
      width: 'w-28',
      className: 'whitespace-nowrap',
      // Sort chronologically, not by the dd-mm-yyyy string.
      sortValue: (i) => toNativeDate(i.deliveryDate) || '',
      exportValue: (i) => i.deliveryDate || '',
      render: (i) => (
        <DeliveryDateCell
          invoice={i}
          disabled={readOnly || inTrash}
          busy={pendingDateId === i.id}
          onChange={(deliveryDate) => deliveryMut.mutate({ id: i.id, deliveryDate })}
        />
      ),
    },
    {
      key: 'trialDate',
      header: 'Trial on',
      width: 'w-28',
      className: 'whitespace-nowrap',
      sortValue: (i) => toNativeDate(i.trialDate) || '',
      exportValue: (i) => i.trialDate || '',
      render: (i) => (
        <TrialDateCell
          invoice={i}
          disabled={(readOnly && !deskOnly) || inTrash}
          busy={pendingTrialDateId === i.id}
          onChange={(trialDate) => trialDateMut.mutate({ id: i.id, trialDate })}
        />
      ),
    },
    {
      key: 'reachedTrial',
      header: 'At store (trial)',
      width: 'w-20',
      sortValue: (i) => (Number(i.itemsTotal) ? Number(i.reachedTrialCount) / Number(i.itemsTotal) : 2),
      exportValue: (i) => `${i.reachedTrialCount || 0}/${i.itemsTotal || 0}`,
      render: (i) => (
        <AtStoreCell
          invoice={i}
          count={i.reachedTrialCount}
          label="for the trial"
          leg="trial"
          disabled={(readOnly && !deskOnly) || inTrash}
          busy={pendingReached === `${i.id}-trial`}
          onChange={(done) => reachedMut.mutate({ id: i.id, leg: 'trial', done })}
        />
      ),
    },
    {
      key: 'trialDone',
      header: 'Trial',
      width: 'w-20',
      sortValue: (i) => (!i.trialDate ? 2 : (Number(i.trialDoneCount) || 0) / (Number(i.itemsTotal) || 1)),
      exportValue: (i) => (!i.trialDate ? '' : `${i.trialDoneCount || 0}/${i.itemsTotal || 0}`),
      render: (i) => (
        <TrialCell
          invoice={i}
          disabled={(readOnly && !deskOnly) || inTrash}
          busy={pendingTrialId === i.id}
          onChange={(done) => trialMut.mutate({ id: i.id, done })}
        />
      ),
    },
    {
      key: 'reachedDelivery',
      header: 'At store (delivery)',
      width: 'w-20',
      sortValue: (i) => (Number(i.itemsTotal) ? Number(i.reachedDeliveryCount) / Number(i.itemsTotal) : 2),
      exportValue: (i) => `${i.reachedDeliveryCount || 0}/${i.itemsTotal || 0}`,
      render: (i) => (
        <AtStoreCell
          invoice={i}
          count={i.reachedDeliveryCount}
          label="for collection"
          leg="delivery"
          disabled={(readOnly && !deskOnly) || inTrash}
          busy={pendingReached === `${i.id}-delivery`}
          onChange={(done) => reachedMut.mutate({ id: i.id, leg: 'delivery', done })}
        />
      ),
    },
    {
      key: 'totalAmount',
      header: 'Total',
      align: 'right',
      width: 'w-24',
      className: 'whitespace-nowrap',
      sortValue: (i) => num(i.totalAmount),
      exportValue: (i) => num(i.totalAmount),
      render: (i) => <span className="font-semibold">{inr(i.totalAmount)}</span>,
    },
    {
      key: 'gstTotal',
      header: 'GST',
      align: 'right',
      width: 'w-20',
      className: 'whitespace-nowrap',
      defaultHidden: true,
      sortValue: (i) => num(i.gstTotal),
      exportValue: (i) => num(i.gstTotal),
      render: (i) => inr(i.gstTotal),
    },
    {
      key: 'receivedAmount',
      header: 'Received',
      align: 'right',
      width: 'w-24',
      className: 'whitespace-nowrap',
      sortValue: (i) => num(i.receivedAmount),
      exportValue: (i) => num(i.receivedAmount),
      render: (i) => <span className="text-success-600">{inr(i.receivedAmount)}</span>,
    },
    {
      key: 'dueAmount',
      header: 'Due',
      align: 'right',
      width: 'w-24',
      className: 'whitespace-nowrap',
      sortValue: (i) => num(i.dueAmount),
      exportValue: (i) => num(i.dueAmount),
      render: (i) => (num(i.dueAmount) > 0
        ? <span className="font-semibold text-danger-600">{inr(i.dueAmount)}</span>
        : <span className="text-content-subtle">—</span>),
    },
    {
      key: 'progress',
      header: 'Progress',
      width: 'w-32',
      // Least finished first: those are the orders that need chasing.
      sortValue: (i) => (Number(i.itemsTotal) ? Number(i.itemsReady) / Number(i.itemsTotal) : 2),
      exportValue: (i) => `${i.itemsReady || 0}/${i.itemsTotal || 0} ready`,
      render: (i) => <ProgressCell invoice={i} />,
    },
    {
      key: 'stage',
      header: 'Stage',
      width: 'w-32',
      // Replaced by Progress, which says the same thing per garment. Kept so
      // anyone who wants the single chip back can switch it on in Columns.
      defaultHidden: true,
      sortValue: (i) => i.stage || '',
      exportValue: (i) => (i.stage ? Object.entries(i.stageCounts || {}).map(([k, n]) => k + ':' + n).join(' ') : ''),
      render: (i) => (
        <StageCell
          invoice={i}
          // The desk logins exist to move an order along, so the stage stays
          // theirs even though everything else on the row is read-only.
          disabled={(readOnly && !deskOnly) || inTrash}
          busy={pendingStageId === i.id}
          onChange={(stage) => stageMut.mutate({ id: i.id, stage })}
        />
      ),
    },
    {
      key: 'status',
      header: 'Status',
      width: 'w-28',
      headerTitle: STATUS_TITLE,
      exportValue: (i) => i.status || '',
      render: (i) => (
        <StatusCell
          invoice={i}
          disabled={(readOnly && !deskOnly) || inTrash}
          busy={pendingId === i.id}
          onChange={(status) => statusMut.mutate({ id: i.id, status })}
        />
      ),
    },
    // Only in the trash: when it was put away, and the way back.
    ...(inTrash ? [{
      key: 'deletedAt',
      header: 'Trashed',
      sortable: false,
      hideable: false,
      exportValue: (i) => i.deletedAt || '',
      render: (i) => (
        <span className="flex flex-wrap items-center gap-2">
          <span className="text-content-muted">{i.deletedAt || '—'}</span>
          <button
            className="btn-outline btn-sm"
            disabled={restoreMut.isPending}
            onClick={(e) => { e.stopPropagation(); restoreMut.mutate(i.id); }}
            title="Put this order back on the list"
          >
            <RotateCcw size={13} /> Restore
          </button>
        </span>
      ),
    }] : []),
  ];

  return (
    <div>
      <PageHeader
        icon={inTrash ? Trash2 : Receipt}
        title={inTrash ? 'Trash' : viewerMode ? 'Order / Invoice Viewer' : 'Orders'}
        subtitle={inTrash
          ? 'Orders that were put away. Nothing is deleted — restore one to put it back on the list.'
          : deskOnly
            ? "The shop's orders. Move an order's status and stage along as the work goes."
            : readOnly
              ? "Browse the shop's orders"
              : 'Create, track and manage all tailoring orders'}
        actions={
          <>
            {/* The trash is the same list, filtered — so it is a switch here
                rather than a menu of its own. */}
            {maySeeTrash && (
              <button
                className={inTrash ? 'btn-primary flex-1 sm:flex-none' : 'btn-outline flex-1 sm:flex-none'}
                onClick={() => setView(inTrash ? 'active' : 'trash')}
              >
                {inTrash ? <><Receipt size={15} /> Back to orders</> : <><Trash2 size={15} /> Trash</>}
              </button>
            )}
            {/* The board is its own switch, so it is offered on its own: a
                login may take orders without being given the floor. */}
            {!inTrash && !viewerMode && can('production') && (
              <button className="btn-outline flex-1 sm:flex-none" onClick={() => nav('/production')}>
                <Factory size={15} /> Production pipeline
              </button>
            )}
            {!readOnly && !inTrash && (
              <button className="btn-primary flex-1 sm:flex-none" onClick={() => nav('/invoices/new')}>
                <Plus size={15} /> New Invoice
              </button>
            )}
          </>
        }
      />

      <DataTable
        columns={columns}
        rows={invoices}
        loading={isLoading}
        storageKey={inTrash ? 'invoices-trash' : 'invoices'}
        searchPlaceholder="Search invoice no., customer, mobile…"
        searchKeys={['orderNo', 'customer', 'mobile', 'status']}
        exportable
        exportFilename="invoices"
        onRowClick={open}
        // The caret opens the work on that order in place: who is making each
        // garment, and when they started and finished.
        renderExpanded={(inv) => <OrderWorkPanel invoiceId={inv.id} />}
        searchAddLabel={readOnly ? undefined : (q) => (isName(q) ? 'Add new customer' : null)}
        onSearchEnter={readOnly ? undefined : (q, _n, offered) => { if (offered) setQuickAdd(q); }}
        emptyTitle={inTrash ? 'The trash is empty' : 'No invoices found'}
        emptyHint={inTrash
          ? 'Orders you put away show up here, and can be restored.'
          : hasRange
            ? `No orders with that ${DATE_LABEL[range.dateField || 'orderDate']} in this range.`
            : 'Create your first invoice to get started.'}
        toolbarExtra={
          // The date range is four controls wide; on a phone it wraps into
          // rows of its own rather than pushing the toolbar sideways.
          <div className="flex w-full flex-wrap items-center gap-1.5 sm:w-auto">
            {/* Our own dropdown, not a native <select>: the browser draws a
                select's list itself and will happily run it off the side of a
                phone, where nothing in the page can reach it. */}
            <span className="w-full sm:w-36">
              <ChipPicker
                header="Filter on which date"
                title="Which date the range applies to"
                ariaLabel="Which date to filter on"
                options={DATE_FIELDS.map((f) => ({ ...f, selected: f.value === dateField }))}
                onPick={(v) => {
                  setDateField(v);
                  if (from || to) setRange({ from, to, dateField: v }); // re-run at once
                }}
              >
                <span className="input input-sm flex w-full items-center justify-between">
                  {DATE_FIELDS.find((f) => f.value === dateField)?.label}
                </span>
              </ChipPicker>
            </span>
            {/* The two dates keep a row to themselves on a phone. They were
                sharing one with the arrow and Apply, which squeezed each down
                to the calendar icon — a date field narrower than about 110px
                has no room left to print dd-mm-yyyy. */}
            <div className="flex w-full items-center gap-1.5 sm:w-auto">
              <DateInput value={from} onChange={setFrom} className="input input-sm min-w-[8rem] flex-1 sm:w-32 sm:flex-none" />
              <span className="shrink-0 text-2xs text-content-subtle">→</span>
              <DateInput value={to} onChange={setTo} className="input input-sm min-w-[8rem] flex-1 sm:w-32 sm:flex-none" />
            </div>
            <button className="btn-outline btn-sm flex-1 sm:flex-none" onClick={apply} disabled={!from && !to}>Apply</button>
            {hasRange && (
              <button className="btn-icon h-7 w-7" onClick={clear} title="Clear date filter">
                <X size={14} />
              </button>
            )}
          </div>
        }
      />

      <QuickAddCustomer
        open={quickAdd != null}
        initial={quickAdd || ''}
        onClose={() => setQuickAdd(null)}
        onCreated={(c) => {
          setQuickAdd(null);
          qc.invalidateQueries({ queryKey: ['customers'] });
          nav(`/invoices/new?customer=${c.id}`);
        }}
      />
    </div>
  );
}

export default function Invoices() {
  return <InvoiceList />;
}
